import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { SectionCard } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { ORDER_STATUSES, formatMinorAmount, formatStatus } from '../../utils/format';

function statusBadgeClass(status) {
  const normalized = status?.toUpperCase();
  if (normalized === 'DELIVERED') return 'badge-success';
  if (normalized === 'CANCELLED' || normalized === 'RETURNED') return 'badge-muted';
  return 'badge-warning';
}

export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const createdId = searchParams.get('created');

  const loadOrders = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listOrders(token, selectedShopId);
      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (createdId) {
      setSuccess(`Order #${createdId} created successfully.`);
      setSearchParams({}, { replace: true });
    }
  }, [createdId, setSearchParams]);

  const openOrder = async (order) => {
    setError(null);
    try {
      const token = await getToken();
      const full = await shopApi.getOrder(token, selectedShopId, order.id);
      setSelectedOrder(full);
      setNewStatus(full.status);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateStatus = async (event) => {
    event.preventDefault();
    if (!selectedOrder) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.updateOrder(token, selectedShopId, selectedOrder.id, { status: newStatus });
      setSelectedOrder(null);
      await loadOrders();
      setSuccess('Order status updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Orders"
        description="View and fulfill customer orders."
        actions={
          <Link to="/shop/orders/new" className="btn btn-primary">
            Create order
          </Link>
        }
      />

      <ErrorBanner message={error} />
      {success && <div className="success-banner">{success}</div>}

      {loading ? (
        <LoadingState />
      ) : orders.length === 0 ? (
        <EmptyState message="No orders yet. Create your first order to get started." />
      ) : (
        <SectionCard title="All orders">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Total</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <strong>#{order.id}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {order.currency}
                    </div>
                  </td>
                  <td>#{order.customer_id}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(order.status)}`}>
                      {formatStatus(order.status)}
                    </span>
                  </td>
                  <td>{formatMinorAmount(order.total_price, order.currency)}</td>
                  <td>{new Date(order.created_at).toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => openOrder(order)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      <Modal
        title={selectedOrder ? `Order #${selectedOrder.id}` : 'Order details'}
        open={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setSelectedOrder(null)}>
              Close
            </button>
            <button type="submit" form="update-order-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Updating…' : 'Update status'}
            </button>
          </>
        }
      >
        {selectedOrder && (
          <>
            <dl className="detail-meta" style={{ marginBottom: '1rem' }}>
              <div>
                <dt>Reservation</dt>
                <dd>{formatStatus(selectedOrder.reservation_status)}</dd>
              </div>
              <div>
                <dt>Subtotal</dt>
                <dd>{formatMinorAmount(selectedOrder.total_price_without_tax, selectedOrder.currency)}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{formatMinorAmount(selectedOrder.total_price, selectedOrder.currency)}</dd>
              </div>
            </dl>

            {selectedOrder.line_items?.length > 0 && (
              <table className="data-table" style={{ marginBottom: '1rem' }}>
                <thead>
                  <tr>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.line_items.map((item) => (
                    <tr key={item.id}>
                      <td>#{item.variant_id}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {item.price_per_unit} {item.currency}
                      </td>
                      <td>{formatMinorAmount(item.total_price, item.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {selectedOrder.addresses?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Addresses</strong>
                {selectedOrder.addresses.map((address) => (
                  <div key={address.id} style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    <span className="badge badge-info">{formatStatus(address.role)}</span>{' '}
                    {address.full_name}, {address.line1}, {address.city} {address.postal_code}
                  </div>
                ))}
              </div>
            )}

            <form id="update-order-form" className="form-field" onSubmit={handleUpdateStatus}>
              <label htmlFor="order-status">Status</label>
              <select
                id="order-status"
                value={newStatus}
                onChange={(event) => setNewStatus(event.target.value)}
              >
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
            </form>
          </>
        )}
      </Modal>
    </RequireShop>
  );
}
